import JoditEditor from "jodit-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { type Config } from "jodit/types/config";
import ValidatedField, { type ValidatedFieldProps } from "components/forms/ValidatedField";
import { type FormikProps } from "formik";
import { accessTokenFactory } from "auth/userManager";
import { ErrorPositions } from "enums/errorPositions";
import styles from "./WysiwygEditor.module.scss";

const optionGroups1 = [
  "{{Email}}",
  "{{Department}}",
  "{{Title}}",
  "{{FirstName}}",
  "{{LastName}}",
  "{{From}}",
  "{{Company}}",
  "{{DateTime}}",
];

type IJodit = {
  value: string;
  iframe?: void | HTMLIFrameElement;
  selection: {
    insertImage: (url: string, styles: { [key: string]: string } | null, imageWidth: string) => void;
  };
};

const createOptionGroup = (fields: string[], label: string): HTMLOptGroupElement => {
  const group = document.createElement("optgroup");
  group.label = label;
  fields.forEach((field) => {
    const option = document.createElement("option");
    option.classList.add("merge-field-select-option");
    option.value = field;
    option.textContent = field;
    group.appendChild(option);
  });
  return group;
};

const isEditorContentEmpty = (content: string): boolean => {
  const emptyPatterns = ["", "<p><br></p>", "<p></p>", "<div><br></div>", "<div></div>"];

  return emptyPatterns.includes(content.trim());
};

type WysiwygEditorProps<T> = Omit<ValidatedFieldProps, "children"> &
  FormikProps<T> & {
    value: string;
    defaultValue: string;
    onBlur: (propertyName: string) => void;
    campaignId: number;
    entityName: string;
    label?: string;
    disabled?: boolean;
    placeholder?: string;
  };

const WysiwygEditor = <T,>({
  value,
  onBlur,
  campaignId,
  entityName,
  disabled,
  placeholder,
  propertyName,
  setFieldValue,
  handleChange,
  handleBlur,
  defaultValue,
  ...props
}: WysiwygEditorProps<T>) => {
  const [accessToken, setAccessToken] = useState("");

  const fetchAccessToken = async () => {
    const token = await accessTokenFactory();
    setAccessToken(`Bearer ${token}`);
  };

  useEffect(() => {
    fetchAccessToken();
  }, []);

  const editor = useRef<IJodit | null>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    // Only sync when it's an external change (like form reset), not internal typing
    if (editor.current && !isInternalChange.current && editor.current.value !== value) {
      editor.current.value = value || "";
    }
    // Reset the flag after any external change
    isInternalChange.current = false;
  }, [value]);

  const config: DeepPartial<Config> = useMemo(
    () => ({
      readonly: disabled,
      disabled,
      editHTMLDocumentMode: true,
      placeholder,
      allowResizeX: false,
      allowResizeY: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      enableDragAndDropFileToEditor: true,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      globalFullSize: false,
      iframe: process.env.NODE_ENV !== "test",
      iframeStyle: "*{font-family: Arial, sans-serif;} p{margin: 0; padding: 0}",
      height: 400,
      addNewLine: false,
      cleanHTML: {
        removeEmptyElements: false,
        fillEmptyParagraph: false,
        replaceNBSP: false,
        denyTags: "SCRIPT",
      },
      events: {
        blur: () => {
          if (handleBlur) {
            handleBlur({
              target: { name: propertyName },
            });
          }
          if (onBlur && propertyName) {
            onBlur(propertyName);
          }
        },
        change: (newContent: string) => {
          const processedContent = isEditorContentEmpty(newContent) ? "" : newContent;

          handleChange &&
            handleChange({
              target: { name: propertyName, value: processedContent },
            });
          setFieldValue && setFieldValue(propertyName, processedContent, true);
          isInternalChange.current = true;
        },
        afterInit: (editor: IJodit) => {
          if (editor.iframe) {
            const iframeDocument = editor.iframe.contentDocument;
            iframeDocument?.addEventListener(
              "submit",
              (e) => {
                e.preventDefault();
                return false;
              },
              true,
            );
            iframeDocument?.querySelectorAll("form").forEach((form) => {
              form.onsubmit = (e) => {
                e.preventDefault();
                return false;
              };
            });

            if (disabled) {
              iframeDocument?.addEventListener(
                "click",
                (e) => {
                  const target = e.target as HTMLElement;
                  const closestLink = target.closest("a");
                  if (closestLink) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }
                },
                true,
              );
            }
          }
        },
      },
      uploader: {
        url: `${envtConfig.apiUrl}/api/v2/threat-defence/campaigns/${campaignId}/${entityName}/upload-image`,
        headers: {
          Authorization: accessToken || "",
        },
        prepareData: (formData: FormData) => {
          const imageFile = formData.get("files[0]");
          if (imageFile) {
            formData.append("selectedFile", imageFile as File);
          }
          return formData;
        },
        isSuccess: (response: Record<string, unknown>) => {
          return response.success;
        },
        defaultHandlerSuccess: (data: Record<string, string[]>) => {
          const fieldName = "files";
          const imageWidth = "auto";
          if (data[fieldName] && data[fieldName].length) {
            for (let index = 0; index < data[fieldName].length; index++) {
              editor.current?.selection.insertImage(data.baseurl + data[fieldName][index], null, imageWidth);
            }
          }
        },
      },
      buttons: [
        "source",
        "|",
        "font",
        "fontsize",
        "bold",
        "strikethrough",
        "underline",
        "italic",
        "brush",
        "eraser",
        "|",
        "align",
        "paragraph",
        "ul",
        "ol",
        "outdent",
        "indent",
        "|",
        "image",
        "table",
        "link",
        "hr",
        "|",
        {
          name: "insertLPUrl",
          tooltip: "Insert Landing Page Url",
          iconURL: "https://icon-library.com/images/url-icon/url-icon-23.jpg",
          exec: (editor) => {
            if ("selection" in editor) {
              editor.selection.insertHTML('<a href="{{Url}}"/>Link to landing page</a>');
            }
          },
        },
        "|",
        "undo",
        "redo",
        "copy",
        "paste",
        "|",
        "fullsize",
        "|",
        {
          name: "insertMergeField",
          tooltip: "Merge tags",
          iconURL: "https://icon-library.com/images/url-icon/url-icon-25.jpg",
          popup: (editor) => {
            const onSelected = (e: Event): void => {
              const selectedMergeField = (e.target as HTMLSelectElement).value;
              if (selectedMergeField && "selection" in editor) {
                editor.selection.insertHTML(selectedMergeField);
              }
            };

            const divElement = editor.create.div("merge-field-popup");

            const labelElement = document.createElement("label");
            labelElement.setAttribute("class", "merge-field-label");
            labelElement.textContent = "Merge tag: ";
            divElement.appendChild(labelElement);

            const selectElement = document.createElement("select");

            const resetValue = (): void => {
              selectElement.value = "";
            };

            selectElement.setAttribute("class", "merge-field-select");
            selectElement.appendChild(createOptionGroup(optionGroups1, "Global variables"));
            selectElement.addEventListener("change", onSelected);
            selectElement.addEventListener("click", resetValue);
            divElement.appendChild(selectElement);

            return divElement;
          },
        },
        {
          name: "insertSubmitForm",
          tooltip: "Insert Submit form",
          iconURL: "https://icon-library.com/images/submit-icon/submit-icon-25.jpg",
          exec: (editor) => {
            const form =
              '<form autocomplete="off">' +
              '<label> Username:</label > <input type="text" /> <br />' +
              '<label> Password:</label > <input type="password" /> <br />' +
              '<input id="login" type="submit" value="Login" /></form ><br />';
            if ("selection" in editor) {
              editor.selection.insertHTML(form);
            }
          },
        },
      ],
    }),
    [
      accessToken,
      campaignId,
      entityName,
      disabled,
      handleBlur,
      handleChange,
      onBlur,
      placeholder,
      propertyName,
      setFieldValue,
    ],
  );

  return (
    <ValidatedField {...props} value={value} propertyName={propertyName} errorPosition={ErrorPositions.top}>
      <div className={styles.wysiwygEditorWrapper}>
        <JoditEditor
          value={defaultValue || undefined}
          config={config as any}
          tabIndex={1}
          ref={(ref) => (editor.current = ref)}
        />
      </div>
    </ValidatedField>
  );
};

export default WysiwygEditor;
